import React, { startTransition, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Rocket,
  Bot,
  Satellite,
  Users,
  Map as MapIcon,
  ArrowRight,
  PlayCircle,
  FileText,
  LineChart,
  Sparkles,
  GitCompare,
  Cloud,
} from 'lucide-react';
import { Button } from '../components/common/Button';
import PackPreviewMock from '../components/marketing/PackPreviewMock';
import ProcessJourneyStrip from '../components/marketing/ProcessJourneyStrip';
import { GA_FUNNEL, trackEvent } from '../utils/gaEvents';
import { afterNextPaint, prefetchMarketingRoutesIdle, prefetchRoute } from '../utils/prefetchRoutes';
import { getSolutionCtaPath, SOLUTION_PAGES } from '../config/solutionPages';

type FaqItem = { id: string; q: string; a: string };

/** Landing solutions strip — first six ICP job spokes (not a full hub clone). */
const LANDING_FEATURED_SOLUTION_SLUGS = [
  'board-pack',
  'hld',
  'estimation',
  'rfp',
  'compare',
  'migration',
] as const;

const LANDING_CTA_FULL = getSolutionCtaPath('board-pack');
const LANDING_CTA_ESTIMATE = getSolutionCtaPath('estimation');

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const allFaqItems = t('pages.faq.items', { returnObjects: true }) as FaqItem[];
  const faqById = useMemo(() => {
    const map = new Map<string, FaqItem>();
    if (Array.isArray(allFaqItems)) {
      for (const item of allFaqItems) {
        map.set(item.id, item);
      }
    }
    return map;
  }, [allFaqItems]);

  const teaserIds = t('pages.landing.faq_teaser_ids', { returnObjects: true }) as string[];
  const faqTeaserItems = (Array.isArray(teaserIds) ? teaserIds : [])
    .map((id) => faqById.get(id))
    .filter((item): item is FaqItem => Boolean(item));

  const featuredSolutions = useMemo(
    () =>
      LANDING_FEATURED_SOLUTION_SLUGS.map((slug) =>
        SOLUTION_PAGES.find((p) => p.slug === slug),
      ).filter((p): p is (typeof SOLUTION_PAGES)[number] => Boolean(p)),
    [],
  );

  useEffect(() => {
    prefetchMarketingRoutesIdle();
  }, []);

  const handleStartWizard = () => {
    prefetchRoute('start');
    startTransition(() => {
      navigate('/start');
    });
    afterNextPaint(() => {
      trackEvent(GA_FUNNEL.CTA_START, { source: 'landing_hero' });
    });
  };

  const handleHowItWorks = () => {
    prefetchRoute('how-it-works');
    startTransition(() => {
      navigate('/how-it-works');
    });
  };

  const handleWhyUs = () => {
    prefetchRoute('why-us');
    startTransition(() => {
      navigate('/why-us');
    });
  };

  const trackFeatureNav = (source: string) => {
    afterNextPaint(() => {
      trackEvent(GA_FUNNEL.CTA_START, { source });
    });
  };

  return (
    <div className="min-h-screen text-white font-sans flex flex-col relative">

      {/* HERO — brand + copy + CTA + product proof (board pack) as visual anchor */}
      <section className="relative isolate px-6 pt-10 sm:pt-14 lg:px-8 flex-grow flex items-center min-h-[min(100dvh,52rem)]">
        <div className="mx-auto max-w-6xl w-full py-10 sm:py-14 lg:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] gap-10 lg:gap-14 xl:gap-16 items-center">

            <div className="landing-hero-copy text-center lg:text-left">
              <p className="mb-3 sm:mb-4 text-sm sm:text-base font-semibold tracking-wide text-primary-light font-poppins">
                {t('pages.landing.hero_brand')}
              </p>

              <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl mb-5 sm:mb-6 font-poppins text-balance">
                {t('pages.landing.title')}{' '}
                <br className="hidden sm:block" />
                <span className="text-primary-light">
                  {t('pages.landing.title_highlight')}
                </span>
              </h1>

              <p className="text-base sm:text-lg leading-7 sm:leading-8 text-gray-300 max-w-xl mx-auto lg:mx-0 text-pretty">
                {t('pages.landing.subtitle')}
              </p>

              <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center lg:justify-start gap-3 sm:gap-4 w-full max-w-xl mx-auto lg:mx-0">
                <Button
                  onClick={handleStartWizard}
                  onMouseEnter={() => prefetchRoute('start')}
                  onFocus={() => prefetchRoute('start')}
                  variant="primary"
                  size="lg"
                  allowWrap
                  icon={<Rocket className="shrink-0" />}
                  className="w-full sm:w-auto px-5 sm:px-8 py-3.5 sm:py-4 text-base sm:text-lg shadow-lg shadow-green-500/25"
                >
                  {t('pages.landing.ctaPrimary')}
                </Button>
                <Button
                  onClick={handleHowItWorks}
                  onMouseEnter={() => prefetchRoute('how-it-works')}
                  onFocus={() => prefetchRoute('how-it-works')}
                  variant="ghost"
                  size="lg"
                  allowWrap
                  icon={<PlayCircle className="text-xl shrink-0" />}
                  className="w-full sm:w-auto px-5 sm:px-7 text-base sm:text-lg hover:bg-white/5 border border-white/10"
                >
                  {t('pages.landing.ctaSecondary')}
                </Button>
              </div>

              <p className="mt-4 sm:mt-5">
                <button
                  type="button"
                  onClick={handleWhyUs}
                  onMouseEnter={() => prefetchRoute('why-us')}
                  onFocus={() => prefetchRoute('why-us')}
                  className="text-sm font-medium text-gray-400 hover:text-green-400 transition-colors inline-flex items-center justify-center lg:justify-start gap-1.5 max-w-full text-balance"
                >
                  {t('pages.landing.ctaTertiary')}
                  <ArrowRight className="w-4 h-4 shrink-0" aria-hidden />
                </button>
              </p>

              <p className="mt-3 text-xs text-gray-500 uppercase tracking-widest">
                {t('pages.landing.ctaSubtext')}
              </p>
            </div>

            <div className="landing-hero-proof w-full flex justify-center lg:justify-end">
              <PackPreviewMock />
            </div>
          </div>
        </div>
      </section>

      {/* Vision strip — positioning vs generic AI */}
      <section className="border-t border-white/5 bg-primary/[0.04] px-6 py-10 sm:py-12 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-white font-poppins text-balance">
            {t('pages.landing.vision_strip_title')}
          </h2>
          <p className="mt-3 text-sm sm:text-base text-gray-300 leading-relaxed text-pretty">
            {t('pages.landing.vision_strip_body')}
          </p>
        </div>
      </section>

      <div className="border-t border-white/5 px-6 py-12 sm:py-16 lg:px-8">
        <ProcessJourneyStrip variant="compact" />
      </div>

      {/* Feature grid */}
      <div className="bg-gray-900/50 py-16 sm:py-24 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">

          <div className="mx-auto max-w-2xl lg:text-center mb-16">
            <h2 className="text-base font-semibold leading-7 text-primary-light">{t('pages.landing.features_eyebrow')}</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              {t('pages.landing.features_heading')}
            </p>
          </div>

          {/* BENTO GRID — WHY outcomes (jobs live in Solutions strip below) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[minmax(250px,auto)]">

            <Link
              to={LANDING_CTA_FULL}
              onClick={() => trackFeatureNav('landing_feature_board_pack')}
              onMouseEnter={() => prefetchRoute('agentic')}
              onFocus={() => prefetchRoute('agentic')}
              className="md:col-span-2 group relative bg-gray-900/50 rounded-2xl p-8 border border-white/5 hover:border-primary/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-primary/10 flex flex-col justify-between"
            >
              <div>
                <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors border border-primary/20">
                  <Bot className="text-primary-light text-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{t('pages.landing.feature_1_title')}</h3>
                <p className="text-gray-400 text-lg leading-relaxed">{t('pages.landing.feature_1_desc')}</p>
              </div>
              <div className="mt-6 flex items-center text-primary-light text-sm font-bold uppercase tracking-wider group-hover:text-green-300">
                {t('pages.landing.feature_1_cta')}<ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/how-it-works"
              onClick={() => trackFeatureNav('landing_feature_how_it_works')}
              onMouseEnter={() => prefetchRoute('how-it-works')}
              onFocus={() => prefetchRoute('how-it-works')}
              className="md:col-span-1 group relative bg-gray-900/50 rounded-2xl p-8 border border-white/5 hover:border-emerald-500/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-emerald-900/20"
            >
              <div className="bg-emerald-500/10 w-12 h-12 rounded-lg flex items-center justify-center mb-6 group-hover:bg-emerald-500/20 transition-colors border border-emerald-500/20">
                <Satellite className="text-emerald-400 text-xl" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{t('pages.landing.feature_2_title')}</h3>
              <p className="text-gray-400 leading-relaxed">{t('pages.landing.feature_2_desc')}</p>
            </Link>

            <Link
              to={LANDING_CTA_ESTIMATE}
              onClick={() => trackFeatureNav('landing_feature_estimation')}
              onMouseEnter={() => prefetchRoute('agentic')}
              onFocus={() => prefetchRoute('agentic')}
              className="md:col-span-1 group relative bg-gray-900/50 rounded-2xl p-8 border border-white/5 hover:border-green-500 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-green-900/20"
            >
              <div className="bg-green-500/10 w-12 h-12 rounded-lg flex items-center justify-center mb-6 group-hover:bg-green-500/20 transition-colors border border-green-500/20">
                <Users className="text-green-400 text-xl" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{t('pages.landing.feature_3_title')}</h3>
              <p className="text-gray-400 leading-relaxed">{t('pages.landing.feature_3_desc')}</p>
            </Link>

            <Link
              to="/roadmap"
              onClick={() => trackFeatureNav('landing_feature_roadmap')}
              onMouseEnter={() => prefetchRoute('roadmap')}
              onFocus={() => prefetchRoute('roadmap')}
              className="md:col-span-2 group relative bg-gray-800/40 rounded-2xl p-8 border border-white/5 hover:border-white/20 transition-all duration-300 hover:shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
            >
              <div className="flex items-center gap-6">
                <div className="bg-white/5 p-4 rounded-full border border-white/10">
                  <MapIcon className="text-gray-300 text-2xl" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-1">
                    {t('pages.landing.feature_4_title')}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {t('pages.landing.feature_4_desc')}
                  </p>
                </div>
              </div>
              <div className="bg-white/10 px-6 py-2 rounded-full text-sm font-semibold group-hover:bg-white/20 transition-colors whitespace-nowrap flex items-center gap-2">
                {t('pages.landing.feature_4_cta')} <span>&rarr;</span>
              </div>
            </Link>

          </div>
        </div>
      </div>

      {/* Solutions strip — WHICH job (SEO spokes); bento above is WHY */}
      <div className="py-16 sm:py-20 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center mb-12">
            <h2 className="text-base font-semibold leading-7 text-emerald-400">{t('nav.solutions')}</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              {t('pages.landing.solutions_heading')}
            </p>
            <p className="mt-4 text-gray-400">{t('pages.landing.solutions_subheading')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {featuredSolutions.map(({ slug }) => (
              <Link
                key={slug}
                to={getSolutionCtaPath(slug)}
                onClick={() => trackFeatureNav(`landing_solution_${slug}`)}
                onMouseEnter={() => prefetchRoute('agentic')}
                onFocus={() => prefetchRoute('agentic')}
                className="group rounded-xl border border-gray-700 bg-gray-900/40 p-5 hover:border-emerald-500/40 transition-colors"
              >
                <div className="mb-3 text-emerald-400">
                  {slug === 'board-pack' && <Rocket className="w-5 h-5" />}
                  {slug === 'hld' && <FileText className="w-5 h-5" />}
                  {slug === 'estimation' && <LineChart className="w-5 h-5" />}
                  {slug === 'rfp' && <Sparkles className="w-5 h-5" />}
                  {slug === 'compare' && <GitCompare className="w-5 h-5" />}
                  {slug === 'migration' && <Cloud className="w-5 h-5" />}
                </div>
                <h3 className="font-semibold text-white text-sm mb-2 group-hover:text-emerald-300">
                  {t(`solutions.pages.${slug}.navLabel`)}
                  {slug === 'rfp' && (
                    <span className="ml-2 align-middle text-[10px] uppercase tracking-wide text-emerald-300 border border-emerald-500/40 rounded px-1.5 py-0.5">
                      {t('hub.badge.pro')}
                    </span>
                  )}
                </h3>
                <p className="text-xs text-gray-400 leading-relaxed line-clamp-3">
                  {t(`solutions.pages.${slug}.cardTeaser`)}
                </p>
              </Link>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link
              to="/solutions"
              className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-400 hover:text-emerald-300"
            >
              {t('solutions.hub.learnMore')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* FAQ teaser — links only; full answers on /faq */}
      <div className="py-16 sm:py-20 bg-gray-900/30 border-t border-white/5">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-white">{t('pages.landing.faq_teaser_heading')}</h2>
            <p className="mt-3 text-gray-400 text-sm sm:text-base">{t('pages.landing.faq_teaser_subheading')}</p>
          </div>
          <div className="space-y-3">
            {faqTeaserItems.map((item) => (
              <Link
                key={item.id}
                to={`/faq#${item.id}`}
                className="flex items-center justify-between gap-3 rounded-lg border border-gray-700 bg-gray-800/50 px-4 py-3 sm:px-5 sm:py-4 text-sm sm:text-base font-medium text-white hover:border-gray-500 hover:bg-gray-800/80 transition-colors"
              >
                <span>{item.q}</span>
                <ArrowRight className="w-4 h-4 shrink-0 text-emerald-400" aria-hidden />
              </Link>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link
              to="/faq"
              className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              {t('pages.landing.faq_teaser_view_all')}
              <ArrowRight className="w-4 h-4" aria-hidden />
            </Link>
          </div>
        </div>
      </div>

    </div>
  );
};

export default LandingPage;
